import {
  Headphones,
  PhoneDisconnect,
  ChatCircle,
  VideoCamera,
} from "@/components/ui/icons";
import { isCallStart, isCallEnd, type MessageKind } from "@ano/shared";
import { Button } from "@/components/ui/button";
import type { RenderUser } from "@/lib/render-user";
import { cn } from "@/lib/utils";

interface SystemMessageRowProps {
  messageId: string;
  kind: MessageKind;
  content: string;
  displayName: string;
  time: string;
  onJoinCall?: (mode: "audio" | "video") => void;
  /** Discord-shape thread-created announcement payload. When
   *  `payload.event === "thread_created"`, render a typed callout
   *  pointing at `payload.thread_message_id`. */
  payload?: Record<string, unknown> | null;
  /** Used to look up the started-by user's display name. */
  userMap?: Map<string, RenderUser>;
  /** Handler for the "started a thread" link — opens the sidebar at
   *  the source message. */
  onThreadClick?: (messageId: string) => void;
}

/**
 * Render branch for messages where `isSystemLike(kind) === true`. Two visual
 * shapes:
 *   • call_started_audio / call_started_video / call_ended → pill with icon
 *     + Join button (when call started + handler provided).
 *   • Everything else (joined_workspace, member_left_channel, etc.) →
 *     plain centered italic line.
 */
export function SystemMessageRow({
  messageId,
  kind,
  content,
  displayName,
  time,
  onJoinCall,
  payload,
  userMap,
  onThreadClick,
}: SystemMessageRowProps) {
  const isCallStarted = isCallStart(kind);
  const isCallEnded = isCallEnd(kind);
  const startedCallMode = kind === "call_started_audio" ? "audio" : "video";
  const startedCallArticle = startedCallMode === "audio" ? "an" : "a";

  // Discord-shape thread-created callout. The mutator stamps
  // `payload.event = "thread_created"` and `payload.thread_message_id`
  // pointing at the source. Click → open sidebar at the source.
  if (
    kind === "system" &&
    payload &&
    typeof payload === "object" &&
    (payload as { event?: unknown }).event === "thread_created"
  ) {
    const threadMessageId = (payload as { thread_message_id?: unknown })
      .thread_message_id;
    const startedBy = (payload as { started_by_user_id?: unknown })
      .started_by_user_id;
    const starterName =
      typeof startedBy === "string"
        ? (userMap?.get(startedBy)?.display_name ?? "Someone")
        : "Someone";
    const targetId =
      typeof threadMessageId === "string" ? threadMessageId : null;
    return (
      <div
        data-message-id={messageId}
        className="py-2 px-4 flex items-center justify-center gap-2"
      >
        <span className="h-px flex-1 max-w-[80px] bg-border/50" />
        <Button
          type="button"
          variant="ghost"
          aria-label={`Open thread started by ${starterName}`}
          onClick={() => targetId && onThreadClick && onThreadClick(targetId)}
          disabled={!targetId || !onThreadClick}
          className={cn(
            "h-auto gap-1.5 px-2 py-1 text-[11px] text-muted-foreground",
            targetId && onThreadClick
              ? "hover:bg-foreground/5 hover:text-foreground"
              : "cursor-default hover:bg-transparent",
          )}
          title="Open thread"
        >
          <ChatCircle size={12} weight="Bold" />
          <span>
            <span className="font-medium text-foreground/80">
              {starterName}
            </span>{" "}
            started a thread
          </span>
          <span className="text-[10px] text-muted-foreground/50">{time}</span>
        </Button>
        <span className="h-px flex-1 max-w-[80px] bg-border/50" />
      </div>
    );
  }

  if (isCallStarted || isCallEnded) {
    return (
      <div
        data-message-id={messageId}
        className="py-1.5 px-4 flex justify-center"
      >
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-lg border px-3 py-2",
            isCallStarted
              ? "border-success/20 bg-success-muted"
              : "border-destructive/20 bg-destructive-muted",
          )}
        >
          {isCallStarted && startedCallMode === "video" && (
            <VideoCamera
              size={14}
              className="text-success shrink-0"
              weight="Bold"
            />
          )}
          {isCallStarted && startedCallMode === "audio" && (
            <Headphones
              size={14}
              className="text-success shrink-0"
              weight="Bold"
            />
          )}
          {isCallEnded && (
            <PhoneDisconnect size={14} className="text-destructive shrink-0" />
          )}
          <span className="text-xs text-muted-foreground">
            {isCallStarted ? (
              <>
                <span className="font-medium text-foreground/70">
                  {displayName}
                </span>{" "}
                started {startedCallArticle} {startedCallMode} call
              </>
            ) : (
              content
            )}
          </span>
          {isCallStarted && onJoinCall && (
            <Button
              type="button"
              size="tiny"
              variant="outline"
              onClick={() => onJoinCall(startedCallMode)}
              className="ml-1 border-success/30 bg-success-muted text-success hover:bg-success/20"
            >
              Join
            </Button>
          )}
          <span className="text-[10px] text-muted-foreground/40">{time}</span>
        </div>
      </div>
    );
  }

  return (
    <div data-message-id={messageId} className="py-2 px-4 text-center">
      <span className="text-xs text-muted-foreground italic">{content}</span>
      <span className="text-[10px] text-muted-foreground/50 ml-2">{time}</span>
    </div>
  );
}
